import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, LinearProgress, MenuItem, Paper, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import useBrandScope from '../corebase/theme/useBrandScope';
import useProjectBase from './useProjectBase';
import { fields, initialRecord, labels, pmApi, valueLabels } from './projectBaseApi';
import './projectbase.css';
import ProjectVisuals from './ProjectVisuals';
const currency = (n, code = 'BOB') => new Intl.NumberFormat('es-BO', {
  style: 'currency',
  currency: code || 'BOB',
  maximumFractionDigits: 0
}).format(n || 0);
const percentage = n => n === null || n === undefined ? 'Sin datos' : Math.round(n * 100) + '%';
const optionLabel = r => r.name + (r.date ? ' · ' + r.date + ' ' + r.startTime : '');
const referenceTypes = ['project', 'activity', 'session', 'resource', 'participant'];
function RecordEditor({
  editor,
  records,
  busy,
  onSave,
  onClose
}) {
  const [data, setData] = useState(() => initialRecord(editor.kind, editor.projectId, editor.record));
  const [error, setError] = useState('');
  const pending = useRef(null);
  const change = (key, value) => {
    pending.current = null;
    setData(d => ({
      ...d,
      [key]: value
    }));
  };
  const submit = async e => {
    e.preventDefault();
    if (!pending.current) pending.current = {
      kind: editor.kind,
      data,
      recordId: editor.record?.id,
      expectedRevision: editor.revision,
      requestId: crypto.randomUUID()
    };
    try {
      await onSave(pending.current);
    } catch (e) {
      setError(e.response?.status === 409 ? 'Los datos cambiaron. Cierra y vuelve a abrir el registro para revisar la versión actual.' : e.response?.data?.message || 'No se confirmó el guardado. Reintenta para recuperar el mismo envío.');
    }
  };
  return <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm"><Box component="form" onSubmit={submit}><DialogTitle>{editor.record?.id ? 'Editar' : 'Registrar'} · {labels[editor.kind]}</DialogTitle><DialogContent><Stack gap={2} pt={1}>{error && <Alert severity="error">{error}</Alert>}<TextField label="Nombre o descripción breve" required value={data.name} onChange={e => change('name', e.target.value)} inputProps={{
            maxLength: 160
          }} />{fields[editor.kind].map(([key, label, type]) => {
            if (type === 'boolean') return <FormControlLabel key={key} label={label} control={<Checkbox checked={Boolean(data[key])} onChange={e => change(key, e.target.checked)} />} />;
            if (Array.isArray(type)) return <TextField key={key} select label={label} value={data[key]} onChange={e => change(key, e.target.value)}>{type.map(v => <MenuItem key={v} value={v}>{valueLabels[v] || v}</MenuItem>)}</TextField>;
            const target = type.replace(/\[\]|\?/g, '');
            if (referenceTypes.includes(target)) {
              const multiple = type.endsWith('[]');
              const available = (records[target] || []).filter(r => !r.archived && r.projectId === editor.projectId && r.id !== editor.record?.id);
              return <TextField key={key} select label={label} required={!multiple && !type.endsWith('?')} SelectProps={{
                multiple
              }} value={data[key] ?? ''} onChange={e => change(key, e.target.value === '' ? null : e.target.value)}>{type.endsWith('?') && <MenuItem value="">Sin actividad</MenuItem>}{available.map(r => <MenuItem key={r.id} value={r.id}>{optionLabel(r)}</MenuItem>)}</TextField>;
            }
            const numeric = type === 'number' || type === 'nullableNumber';
            const inputType = type === 'nullableDate' ? 'date' : type === 'nullableTime' ? 'time' : type;
            return <TextField key={key} label={label} type={numeric ? 'number' : inputType === 'text' ? 'text' : inputType} required={['objective', 'outcome', 'evidence', 'mitigation', 'startDate', 'endDate', 'date', 'startTime', 'endTime', 'dueDate', 'observedOn'].includes(key)} multiline={['objective', 'outcome', 'evidence', 'mitigation'].includes(key)} minRows={['objective', 'outcome', 'evidence', 'mitigation'].includes(key) ? 2 : undefined} InputLabelProps={['date', 'time'].includes(inputType) ? {
              shrink: true
            } : undefined} inputProps={numeric ? {
              min: 0,
              step: ['capacity', 'target'].includes(key) && editor.kind === 'activity' ? 1 : 'any'
            } : {
              maxLength: 2000
            }} value={data[key] ?? ''} onChange={e => change(key, numeric ? e.target.value === '' && type === 'nullableNumber' ? null : Number(e.target.value) : type.startsWith('nullable') && e.target.value === '' ? null : e.target.value)} />;
          })}<TextField label="Notas y supuestos" multiline minRows={2} value={data.notes} onChange={e => change('notes', e.target.value)} />{editor.kind === 'resource' && <Alert severity="info">Horas: disponibilidad por semana. Espacios y equipos en unidades: disponibilidad simultánea. Materiales y dinero: total del programa.</Alert>}{editor.kind === 'project' && <Typography variant="caption">Zona horaria: Bolivia (America/La_Paz).</Typography>}</Stack></DialogContent><DialogActions><Button disabled={busy} onClick={onClose}>Cerrar</Button><Button type="submit" variant="contained" disabled={busy}>{busy ? 'Guardando…' : 'Guardar'}</Button></DialogActions></Box></Dialog>;
}
function ProgramDialog({
  revision,
  busy,
  onSave,
  onClose
}) {
  const [data, setData] = useState({
    endDate: '2027-02-28',
    budget: 0,
    capacity: 25,
    classStartTime: '19:00',
    classEndTime: '20:00',
    workshopStartTime: '20:00',
    workshopEndTime: '21:00'
  });
  const [error, setError] = useState('');
  const pending = useRef(null);
  const submit = async e => {
    e.preventDefault();
    if (!pending.current) pending.current = {
      ...data,
      expectedRevision: revision,
      requestId: crypto.randomUUID()
    };
    try {
      await onSave(pending.current);
    } catch (e) {
      setError(e.response?.data?.message || 'No se confirmó el guardado; reintenta el mismo envío.');
    }
  };
  return <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm"><Box component="form" onSubmit={submit}><DialogTitle>Tu programa de cursos y talleres</DialogTitle><DialogContent><Stack gap={2} pt={1}><Alert severity="info">3 cursos y 5 talleres. Los cursos de miércoles empiezan el 4 de noviembre. Confirma estos valores propuestos antes de crear.</Alert>{error && <Alert severity="error">{error}</Alert>}{Object.entries({
            endDate: 'Última fecha de clases',
            budget: 'Presupuesto (Bs)',
            capacity: 'Cupos y meta por actividad',
            classStartTime: 'Clases: inicio',
            classEndTime: 'Clases: fin',
            workshopStartTime: 'Talleres: inicio',
            workshopEndTime: 'Talleres: fin'
          }).map(([key, label]) => <TextField key={key} label={label} required type={key === 'endDate' ? 'date' : key.includes('Time') ? 'time' : 'number'} InputLabelProps={{
            shrink: true
          }} value={data[key]} inputProps={key === 'endDate' ? {
            min: '2026-11-25',
            max: '2027-02-28'
          } : {
            min: key === 'capacity' ? 1 : 0
          }} onChange={e => {
            pending.current = null;
            setData(d => ({
              ...d,
              [key]: ['budget', 'capacity'].includes(key) ? Number(e.target.value) : e.target.value
            }));
          }} />)}<Typography color="text.secondary" variant="body2">Los dos cursos de miércoles usarán inicialmente la misma franja: ajusta las sesiones antes de confirmar. Facilitadores, espacios y costos quedan por completar. Cancela las sesiones de receso.</Typography></Stack></DialogContent><DialogActions><Button disabled={busy} onClick={onClose}>Cerrar</Button><Button disabled={busy} variant="contained" type="submit">Crear programa</Button></DialogActions></Box></Dialog>;
}
function Metric({
  label,
  value,
  hint
}) {
  return <Paper variant="outlined" sx={{
    p: 2.5
  }}><Typography color="text.secondary" variant="body2">{label}</Typography><Typography variant="h4" sx={{
      fontSize: 28,
      my: 1
    }}>{value}</Typography><Typography variant="caption" color="text.secondary">{hint}</Typography></Paper>;
}
export default function ProjectBaseCore() {
  useBrandScope();
  const store = useProjectBase();
  const [tab, setTab] = useState('overview'),
    [projectId, setProjectId] = useState(''),
    [query, setQuery] = useState(''),
    [status, setStatus] = useState(''),
    [from, setFrom] = useState(''),
    [to, setTo] = useState(''),
    [archived, setArchived] = useState(false),
    [editor, setEditor] = useState(null),
    [program, setProgram] = useState(null),
    [message, setMessage] = useState(null),
    [scenario, setScenario] = useState(null),
    [scenarioBudget, setScenarioBudget] = useState(''),
    [capacityChanges, setCapacityChanges] = useState({}),
    [simulating, setSimulating] = useState(false),
    [archiveAction, setArchiveAction] = useState(null);
  const records = store.snapshot?.records || {};
  const projects = (records.project || []).filter(p => !p.archived);
  const selected = projects.find(p => p.id === projectId) || projects[0];
  const activeId = selected?.id;
  const revision = store.snapshot?.revision || 0;
  // Dashboard is calculated by the server for the selected project, using the same command as MCP.
  const [detail, setDetail] = useState(null);
  const detailKey = useRef('');
  const loadDetail = async (id, rev) => {
    const key = id + ':' + rev;
    detailKey.current = key;
    try {
      const result = await pmApi.command('pm_dashboard', {
        projectId: id
      });
      if (detailKey.current === key) setDetail({
        id,
        revision: result.revision,
        summary: result.summary
      });
    } catch (e) {
      if (detailKey.current === key) setMessage({
        severity: 'error',
        text: e.response?.data?.message || 'No se pudo cargar el resumen.'
      });
    }
  };
  const neededKey = activeId + ':' + revision;
  // Selection and server refresh are connected below through an effect.
  const summary = detail && activeId && detail.id === activeId && detail.revision === revision ? detail.summary : null;
  const refreshDetailRef = useRef(loadDetail);
  refreshDetailRef.current = loadDetail;
  const candidates = (records[tab] || []).filter(r => (archived || !r.archived) && (tab === 'project' || r.projectId === activeId) && (!status || r.status === status) && (!query || (r.name + ' ' + (r.notes || '')).toLowerCase().includes(query.toLowerCase())) && (!from || (r.date || r.dueDate || r.observedOn || r.startDate || '') >= from) && (!to || (r.date || r.dueDate || r.observedOn || r.startDate || '') <= to)).sort((a, b) => (a.date || a.dueDate || a.name).localeCompare(b.date || b.dueDate || b.name));
  const [page, setPage] = useState(0);
  const pageRows = candidates.slice(page * 30, page * 30 + 30);
  const save = async args => {
    await store.execute('pm_save_record', args);
    setEditor(null);
    setMessage({
      severity: 'success',
      text: 'Registro guardado.'
    });
  };
  const createProgram = async args => {
    const result = await store.execute('pm_create_learning_program', args);
    setProgram(null);
    setProjectId(result.record.id);
    setMessage({
      severity: 'info',
      text: result.notes.join(' ')
    });
  };
  const simulate = async () => {
    setSimulating(true);
    try {
      setScenario(await pmApi.command('pm_resource_scenario', {
        projectId: activeId,
        budget: Number(scenarioBudget || selected.budget),
        resourceChanges: Object.entries(capacityChanges).map(([resourceId, capacity]) => ({
          resourceId,
          capacity: Number(capacity)
        }))
      }));
    } catch (e) {
      setMessage({
        severity: 'error',
        text: e.response?.data?.message || 'No se pudo simular.'
      });
    } finally {
      setSimulating(false);
    }
  };
  useEffect(() => {
    if (activeId) refreshDetailRef.current(activeId, revision);
    return () => {
      detailKey.current = '';
    };
  }, [activeId, revision, neededKey]);
  useEffect(() => {
    setScenario(null);
  }, [revision]);
  const openEditor = (kind, record) => setEditor({
    kind,
    record,
    projectId: activeId,
    revision
  });
  if (!store.snapshot) return <Container><Paper sx={{
      p: 5,
      textAlign: 'center'
    }}>{store.error ? <Alert severity="error" action={<Button onClick={store.refresh}>Reintentar</Button>}>{store.error}</Alert> : <><CircularProgress size={28} /><Typography mt={2}>Preparando tu centro de control…</Typography></>}</Paper></Container>;
  return <Container maxWidth="xl" className="projectbase"><Paper className="pm-hero" sx={{
      p: {
        xs: 3,
        md: 4
      },
      color: 'white',
      background: 'linear-gradient(115deg,#0B213A,#173E68)',
      mb: 3
    }}><Stack direction={{
        xs: 'column',
        md: 'row'
      }} justifyContent="space-between" gap={2}><Box><Typography variant="overline" sx={{
            color: '#91CAFF'
          }}>PROJECTBASE PM</Typography><Typography variant="h4" sx={{
            mt: 1
          }}>Del propósito a los resultados</Typography><Typography sx={{
            mt: 1,
            color: '#CDDBEC'
          }}>Planifica lo necesario. Cuida los recursos. Comprueba el beneficio.</Typography></Box><Stack direction="row" gap={1} alignItems="center" flexWrap="wrap"><Chip label={store.busy ? 'Guardando…' : store.error ? 'Revisar conexión' : 'Guardado en la nube'} sx={{
            color: 'white',
            bgcolor: '#ffffff18'
          }} /><Button startIcon={<RefreshIcon />} sx={{
            color: 'white'
          }} onClick={store.refresh} disabled={store.busy}>Actualizar</Button></Stack></Stack></Paper>
 {store.error && <Alert severity="error" sx={{
      mb: 2
    }}>{store.error}</Alert>}{message && <Alert severity={message.severity} sx={{
      mb: 2
    }} onClose={() => setMessage(null)}>{message.text}</Alert>}
 <Stack direction={{
      xs: 'column',
      md: 'row'
    }} justifyContent="space-between" gap={2} mb={3}><Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">{projects.length > 0 && <TextField select size="small" label="Proyecto" value={activeId || ''} sx={{
          minWidth: 240
        }} onChange={e => {
          setProjectId(e.target.value);
          setScenario(null);
          setCapacityChanges({});
          setScenarioBudget('');
          setPage(0);
        }}>{projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}</TextField>}<Button onClick={() => openEditor('project')} startIcon={<AddIcon />}>Nuevo proyecto</Button></Stack><Stack direction="row" gap={1}><Button variant="outlined" onClick={() => setProgram({
          revision
        })}>Usar mi programa educativo</Button><Button component={Link} to="/account/mcp-access">Acceso del asistente</Button></Stack></Stack>
 <Paper variant="outlined" sx={{
      mb: 3,
      overflow: 'hidden'
    }}><Tabs variant="scrollable" scrollButtons="auto" value={tab} onChange={(_, v) => {
        setTab(v);
        setPage(0);
        setStatus('');
        setQuery('');
        setFrom('');
        setTo('');
      }}><Tab value="overview" label="Centro de control" /><Tab value="visuals" label="PM Star · 5 vistas" />{Object.entries(labels).map(([key, label]) => <Tab key={key} value={key} label={label} />)}<Tab value="scenario" label="Escenarios" /></Tabs></Paper>
 {!projects.length && tab !== 'project' ? <Paper variant="outlined" sx={{
      p: 5,
      textAlign: 'center'
    }}><FactCheckOutlinedIcon sx={{
        fontSize: 48,
        color: 'primary.main'
      }} /><Typography variant="h5" my={2}>Empieza con un resultado que quieras lograr</Typography><Typography color="text.secondary" mb={3}>Crea un proyecto vacío o utiliza tu programa de cursos y talleres. Después confirma personas, horarios y recursos.</Typography><Button variant="contained" onClick={() => openEditor('project')}>Crear mi primer proyecto</Button></Paper> : tab === 'visuals' ? <ProjectVisuals key={activeId} projectId={activeId} project={selected} records={records} revision={revision} busy={store.busy} onEdit={openEditor} execute={store.execute} /> : tab === 'overview' ? <>
 {!summary ? <LinearProgress /> : <><Box className="pm-metrics"><Metric label="Presupuesto disponible" value={currency(summary.remainingBudget, selected.currency)} hint={`Previsto: ${currency(summary.cost, selected.currency)}`} /><Metric label="Sesiones completadas" value={`${summary.completedSessions} / ${summary.sessions}`} hint="Ejecución registrada" /><Metric label="Logros comprobados" value={summary.achieved} hint="Resultados por actividad con evidencia" /><Metric label="Asistencia registrada" value={percentage(summary.attendanceRate)} hint={`Cobertura del registro: ${percentage(summary.attendanceCoverage)}`} /></Box><Box className="pm-panels"><Paper variant="outlined" sx={{
            p: 3
          }}><Typography variant="h6" mb={2}>Qué necesita atención</Typography>{!summary.alerts.length ? <Alert severity="success">No hay alertas en los datos registrados. Revisa también los supuestos.</Alert> : summary.alerts.map((a, i) => <Alert key={i} severity={a.type === 'conflict' || a.type === 'budget' ? 'error' : 'warning'} sx={{
              mb: 1.5
            }}><strong>{a.name}</strong><Typography variant="body2">{a.action}</Typography><Typography variant="caption">{a.evidence.length} registros respaldan esta señal.</Typography><Button size="small" onClick={() => {
                const found = Object.entries(records).flatMap(([kind, items]) => items.filter(r => a.evidence.includes(r.id) && !r.archived).map(record => ({
                  kind,
                  record
                })));
                if (found[0]) openEditor(found[0].kind, found[0].record);
              }}>Revisar registro</Button></Alert>)}</Paper><Stack gap={2}><Paper variant="outlined" sx={{
              p: 3
            }}><Typography variant="h6" mb={2}>Resultados que buscamos</Typography>{summary.outcomes.map(o => <Box key={o.id} mb={2}><Typography variant="subtitle2">{o.name}</Typography><Typography variant="body2" color="text.secondary">{o.achieved} logros · meta {o.target} · {o.enrolled} inscritos</Typography><LinearProgress sx={{
                  mt: 1,
                  height: 6,
                  borderRadius: 4
                }} variant="determinate" value={Math.min(100, (o.progress || 0) * 100)} /></Box>)}{!summary.outcomes.length && <Typography color="text.secondary">Registra actividades y una meta verificable.</Typography>}</Paper><Paper variant="outlined" sx={{
              p: 3
            }}><Typography variant="h6" mb={2}>Uso y disponibilidad de recursos</Typography>{summary.resourceUsage.map(r => <Box key={r.id} mb={1.5}><Typography fontWeight={600}>{r.name}</Typography><Typography variant="body2" color="text.secondary">{r.remaining} {valueLabels[r.unit] || r.unit} libres previstos · {r.basis}</Typography><Typography variant="caption" color="text.secondary">Previsto: {r.planned} · uso real acumulado: {r.actual === null ? "sin registrar" : r.actual}</Typography></Box>)}{!summary.resourceUsage.length && <Typography color="text.secondary">Sin recursos registrados.</Typography>}</Paper></Stack></Box><Alert severity="info" sx={{
          mt: 3
        }}>Las alertas usan reglas verificables. No son predicciones de IA. Las horas se comparan por semana; espacios y equipos en unidades por reservas simultáneas; materiales y dinero por programa. No dupliques costos base y costos de recursos.</Alert></>}
 </> : tab === 'scenario' ? <Paper variant="outlined" sx={{
      p: 3
    }}><Typography variant="h5">Decide antes de ajustar</Typography><Typography color="text.secondary" my={2}>Prueba otro presupuesto y disponibilidad. La simulación conserva tus datos y muestra criterios, dependencias y compromisos.</Typography><Stack gap={2} sx={{
        maxWidth: 650
      }}><TextField label={`Presupuesto del escenario (${selected.currency})`} type="number" value={scenarioBudget} placeholder={String(selected.budget)} inputProps={{
          min: 0
        }} onChange={e => {
          setScenarioBudget(e.target.value);
          setScenario(null);
        }} />{(records.resource || []).filter(r => !r.archived && r.projectId === activeId).map(r => <TextField key={r.id} type="number" label={`${r.name} · ${valueLabels[r.unit] || r.unit}`} value={capacityChanges[r.id] ?? r.capacity} inputProps={{
          min: 0
        }} onChange={e => {
          setCapacityChanges(c => ({
            ...c,
            [r.id]: e.target.value
          }));
          setScenario(null);
        }} />)}<Button variant="contained" disabled={simulating} onClick={simulate}>{simulating ? 'Calculando…' : 'Comparar escenario'}</Button></Stack>{scenario && <Box mt={3}><Alert severity={scenario.warning ? 'warning' : 'info'}>{scenario.warning || scenario.method} No se aplicaron cambios. {scenario.feasible === false ? "Hay restricciones pendientes: revisa las alertas antes de ejecutar." : ""}</Alert><Typography mt={2} fontWeight={700}>Saldo: {currency(scenario.remaining, selected.currency)}</Typography><Box className="pm-panels" sx={{
          mt: 2
        }}>{[['Conservar en el escenario', scenario.selected], ['Revisar o posponer', scenario.deferred]].map(([title, items]) => <Paper key={title} variant="outlined" sx={{
            p: 2
          }}><Typography variant="h6" mb={2}>{title}</Typography>{items.map(a => <Box key={a.id} mb={2}><Typography fontWeight={600}>{a.name}</Typography><Typography variant="body2">{currency(a.totalCost, selected.currency)} · {a.reason || valueLabels[a.priority]} {a.protected ? '· Compromiso protegido' : ''}</Typography></Box>)}</Paper>)}</Box>{scenario.alerts.map((a, i) => <Alert severity="warning" sx={{
          mt: 1
        }} key={i}>{a.name}: {a.action}</Alert>)}<Typography variant="subtitle1" mt={3}>Si hay recursos disponibles</Typography>{scenario.surplusIdeas.map(r => <Typography key={r.resourceId} variant="body2" my={1}>{r.name}: {r.available} {valueLabels[r.unit] || r.unit}. {r.suggestion}</Typography>)}</Box>}</Paper> : <>
 <Stack direction={{
        xs: 'column',
        md: 'row'
      }} gap={1.5} mb={2} flexWrap="wrap"><TextField size="small" label="Buscar" value={query} onChange={e => {
          setQuery(e.target.value);
          setPage(0);
        }} /><TextField size="small" label="Estado" select value={status} onChange={e => {
          setStatus(e.target.value);
          setPage(0);
        }} sx={{
          minWidth: 150
        }}><MenuItem value="">Todos</MenuItem>{[...new Set((records[tab] || []).map(r => r.status).filter(Boolean))].map(v => <MenuItem key={v} value={v}>{valueLabels[v] || v}</MenuItem>)}</TextField><TextField size="small" type="date" label="Desde" InputLabelProps={{
          shrink: true
        }} value={from} onChange={e => {
          setFrom(e.target.value);
          setPage(0);
        }} /><TextField size="small" type="date" label="Hasta" InputLabelProps={{
          shrink: true
        }} value={to} onChange={e => {
          setTo(e.target.value);
          setPage(0);
        }} /><FormControlLabel label="Ver archivados" control={<Checkbox checked={archived} onChange={e => {
          setArchived(e.target.checked);
          setPage(0);
        }} />} /><Button variant="contained" startIcon={<AddIcon />} onClick={() => openEditor(tab)} disabled={store.busy}>Registrar</Button></Stack>
 <Box className="pm-records">{pageRows.map(r => <Paper key={r.id} variant="outlined" sx={{
          p: 2.5
        }}><Stack direction="row" justifyContent="space-between" gap={1}><Typography variant="h6">{r.name}</Typography>{(r.status || r.archived) && <Chip size="small" label={r.archived ? 'Archivado' : valueLabels[r.status] || r.status} />}</Stack><Box my={2}>{fields[tab].map(([key, label, type]) => {
              const value = r[key];
              if (value === undefined || value === null || value === '' || key === 'dependencies') return null;
              let display = Array.isArray(value) ? value.map(id => (records[type.replace('[]', '')] || []).find(x => x.id === id)?.name || 'Registro archivado').join(', ') : typeof value === 'boolean' ? value ? 'Sí' : 'No' : typeof type === 'string' && referenceTypes.includes(type.replace('?', '')) ? (records[type.replace('?', '')] || []).find(x => x.id === value)?.name || 'Registro archivado' : valueLabels[value] || String(value);
              return <Typography key={key} variant="body2" color="text.secondary" my={0.5}><strong>{label}:</strong> {display}</Typography>;
            })}{r.notes && <Typography variant="body2" mt={1}>{r.notes}</Typography>}</Box><Stack direction="row" gap={1}><Button disabled={store.busy || r.archived} onClick={() => openEditor(tab, r)}>Editar</Button><Button color={r.archived ? 'primary' : 'warning'} disabled={store.busy} onClick={() => setArchiveAction({
              kind: tab,
              recordId: r.id,
              archived: !r.archived,
              expectedRevision: revision,
              requestId: crypto.randomUUID(),
              name: r.name
            })}>{r.archived ? 'Restaurar' : 'Archivar'}</Button></Stack></Paper>)}</Box>{!candidates.length && <Paper variant="outlined" sx={{
        p: 4,
        textAlign: 'center'
      }}><Typography>No hay registros que coincidan. Registra el primero o ajusta los filtros.</Typography></Paper>}<Stack direction="row" justifyContent="space-between" my={2}><Button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Anterior</Button><Typography variant="caption">{candidates.length} registros · página {page + 1}</Typography><Button disabled={(page + 1) * 30 >= candidates.length} onClick={() => setPage(p => p + 1)}>Siguiente</Button></Stack>
 </>}
 {editor && <RecordEditor key={editor.kind + ':' + (editor.record?.id || 'new')} editor={editor} records={records} busy={store.busy} onSave={save} onClose={() => setEditor(null)} />}{program && <ProgramDialog revision={program.revision} busy={store.busy} onSave={createProgram} onClose={() => setProgram(null)} />}
 {archiveAction && <Dialog open onClose={store.busy ? undefined : () => setArchiveAction(null)}><DialogTitle>{archiveAction.archived ? 'Archivar' : 'Restaurar'} {archiveAction.name}</DialogTitle><DialogContent>Los datos se conservarán. Las referencias activas deben desvincularse antes de archivar.</DialogContent><DialogActions><Button disabled={store.busy} onClick={() => setArchiveAction(null)}>Cerrar</Button><Button disabled={store.busy} onClick={async () => {
          const {
            name,
            ...args
          } = archiveAction;
          try {
            await store.execute('pm_archive_record', args);
            setArchiveAction(null);
          } catch (e) {
            setMessage({
              severity: 'error',
              text: e.response?.data?.message || 'No se confirmó la operación. Reintenta el mismo envío.'
            });
          }
        }}>Confirmar</Button></DialogActions></Dialog>}
 <Typography variant="caption" color="text.secondary" display="block" mt={3}>Espacio personal · {store.updated ? 'Actualizado ' + store.updated.toLocaleTimeString('es-BO', {
        timeZone: 'America/La_Paz'
      }) : 'Sin actualización'} · Los cambios del asistente se consultan automáticamente cada 20 segundos.</Typography>
 </Container>;
}
